import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

/**
 * GDPR cookie/consent banner.
 * - Essential: always on (required for the app to function).
 * - Analytics: opt-in.
 * - AI voice processing: opt-in disclosure (audio sent to ElevenLabs/Deepgram).
 *
 * Consents are stored locally for unauthenticated visitors and additionally
 * persisted to `user_consents` once the user signs in (immutable audit trail).
 */
const STORAGE_KEY = 'na_consent_v1';
const CONSENT_VERSION = '1.0';

type Consents = { essential: true; analytics: boolean; ai_voice: boolean };

const DEFAULTS: Consents = { essential: true, analytics: false, ai_voice: false };

export function readConsents(): Consents | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { ...DEFAULTS, ...parsed };
  } catch {
    return null;
  }
}

async function persistRemote(consents: Consents) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  const rows = (Object.keys(consents) as Array<keyof Consents>).map((k) => ({
    user_id: user.id,
    consent_type: k,
    granted: !!consents[k],
    version: CONSENT_VERSION,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  }));
  await supabase.from('user_consents').insert(rows);
}

export function ConsentBanner() {
  const [open, setOpen] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [consents, setConsents] = useState<Consents>(DEFAULTS);

  useEffect(() => {
    const existing = readConsents();
    if (!existing) setOpen(true);
    else setConsents(existing);
  }, []);

  const save = async (next: Consents) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...next, _v: CONSENT_VERSION, _at: new Date().toISOString() }));
    setConsents(next);
    setOpen(false);
    persistRemote(next).catch(() => {});
  };

  if (!open) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-[60] p-4 sm:p-6">
      <div className="max-w-3xl mx-auto bg-card border border-border/60 rounded-2xl shadow-2xl p-5 sm:p-6 space-y-4">
        <div>
          <h2 className="font-serif text-lg sm:text-xl text-foreground">Your privacy choices</h2>
          <p className="text-sm text-muted-foreground mt-1">
            We use essential cookies to run the app. With your consent we may use analytics to improve the product, and process your voice with our AI partners during simulations. Read our{' '}
            <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link> and{' '}
            <Link to="/cookies" className="underline hover:text-foreground">Cookie Policy</Link>.
          </p>
        </div>

        {showCustomize && (
          <div className="space-y-3 border border-border/40 rounded-xl p-4 bg-background/40">
            <Row label="Essential" desc="Auth, billing, security. Cannot be disabled." checked disabled />
            <Row label="Analytics" desc="Anonymous usage data to improve the product." checked={consents.analytics} onChange={(v) => setConsents({ ...consents, analytics: v })} />
            <Row label="AI voice processing" desc="Stream audio to ElevenLabs/Deepgram during simulations. Required for voice mode." checked={consents.ai_voice} onChange={(v) => setConsents({ ...consents, ai_voice: v })} />
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <Button variant="ghost" onClick={() => save(DEFAULTS)}>Reject non-essential</Button>
          {!showCustomize && (
            <Button variant="outline" onClick={() => setShowCustomize(true)}>Customize</Button>
          )}
          {showCustomize && (
            <Button variant="outline" onClick={() => save(consents)}>Save choices</Button>
          )}
          <Button onClick={() => save({ essential: true, analytics: true, ai_voice: true })}>Accept all</Button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, desc, checked, onChange, disabled }: { label: string; desc: string; checked: boolean; onChange?: (v: boolean) => void; disabled?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="text-sm font-medium text-foreground">{label}</div>
        <div className="text-xs text-muted-foreground">{desc}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}
